import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig.js";

import { CDM_FINDER_REPORT_TYPE } from "../constants/cdmFinder";
import { buildCdmFinderParams } from "../utils/buildCdmFinderParams";
import { getQueryParamsFromObject } from "../utils/getQueryParamsFromObject";
import { getReportPath } from "../utils/getReportPath";

const CDM_RECALL_REPORT_TYPE = "cdm-recall";
const CDM_REGISTER_REPORT_TYPE = "cdm-register";

const buildExportQuery = (reportType, filters) => {
  // The finder needs repeatable array params; the recall/register reports export
  // their on-screen filters too so they don't dump the whole (capped) cohort.
  if (reportType === CDM_FINDER_REPORT_TYPE) {
    return buildCdmFinderParams(filters).replace(/^&/, "");
  }
  if (reportType === CDM_REGISTER_REPORT_TYPE) {
    return getQueryParamsFromObject({
      registerType: "TREATMENT",
      ...filters,
    }).replace(/^\?/, "");
  }
  if (reportType === CDM_RECALL_REPORT_TYPE) {
    return getQueryParamsFromObject(filters).replace(/^\?/, "");
  }

  // All other reports - pass filters to export endpoint
  return getQueryParamsFromObject(filters).replace(/^\?/, "");
};

const exportReports = async ({
  tenantVal,
  reportType,
  filters,
  format = "excel",
}) => {
  const axiosInstance = createAxiosInstance();
  const isPdf = format === "pdf";
  // PDF (KIN-1409 G13) uses the /pdf endpoint; Excel uses /export.
  const action = isPdf ? "pdf" : "export";
  const query = buildExportQuery(reportType, filters);
  const url = `${tenantVal}/reports/${getReportPath(reportType)}/${action}${
    query ? `?${query}` : ""
  }`;

  const response = await axiosInstance.get(url, {
    responseType: "arraybuffer",
  });

  // All reports use XLSX format (XSSFWorkbook/SXSSFWorkbook on backend)
  const extension = isPdf ? "pdf" : "xlsx";
  const blob = new Blob(
    [response.data],
    isPdf ? { type: "application/pdf" } : undefined
  );
  const fileName = `${reportType ?? "patients"}.${extension}`;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const useExportReports = () =>
  useMutation({
    mutationFn: exportReports,
    onError: () => {
      toast.error("Failed to generate report. Please try again.");
    },
  });
